/**
 * WidgetPalette — panneau latéral listant les widgets disponibles pour le
 * builder de rapports Orion (Phase 2a).
 *
 * Porté depuis Metrix (`frontend/src/features/reports/WidgetPalette.tsx`),
 * réduit aux 6 widgets cœur et adapté au pattern drop de `PageCanvas.tsx` :
 * ce dernier attend une prop `draggingType` qu'il set/reset lui-même via
 * les callbacks `onDragStartWidget` / `onDragEndWidget` remontés ici, et
 * react-grid-layout (dropConfig) lit `e.dataTransfer` au drop natif — d'où
 * le `setData('text/plain', type)` en plus du callback.
 */
import {
  AreaChart,
  BarChart3,
  CalendarDays,
  CalendarRange,
  Gauge as GaugeIcon,
  Grid2x2,
  Hash,
  Image as ImageIcon,
  LineChart,
  Map as MapIcon,
  Minus,
  Percent,
  PieChart,
  Scale,
  Table as TableIcon,
  Trophy,
  Type as TypeIcon,
  type LucideIcon,
} from 'lucide-react';
import { widgetCategories, widgetDefaults } from './widgetDefaults';
import type { WidgetType } from './schemas';

export interface WidgetPaletteProps {
  onDragStartWidget: (type: WidgetType) => void;
  onDragEndWidget?: () => void;
}

const ICONS: Record<WidgetType, LucideIcon> = {
  metric_card: Hash,
  comparison_metric: Scale,
  gauge: GaugeIcon,
  progress_bar: Percent,
  line_chart: LineChart,
  area_chart: AreaChart,
  bar_chart: BarChart3,
  donut_pie: PieChart,
  table: TableIcon,
  top_list: Trophy,
  treemap: Grid2x2,
  map: MapIcon,
  calendar_heatmap: CalendarDays,
  divider: Minus,
  image_block: ImageIcon,
  rich_text: TypeIcon,
  date_range_display: CalendarRange,
};

export default function WidgetPalette({ onDragStartWidget, onDragEndWidget }: WidgetPaletteProps) {
  return (
    <aside className="w-56 shrink-0 overflow-y-auto border-r border-[#E2E4ED] bg-white p-3">
      <div className="mb-3 text-xs font-medium uppercase tracking-wide text-[#8B8FA7]">
        Widgets
      </div>
      <div className="space-y-4">
        {widgetCategories.map((cat) => (
          <div key={cat.name}>
            <div className="mb-1.5 text-xs font-medium text-[#8B8FA7]">{cat.name}</div>
            <div className="space-y-1">
              {cat.types.map((type) => {
                const def = widgetDefaults[type];
                const Icon = ICONS[type];
                return (
                  <div
                    key={type}
                    className="group flex cursor-grab items-center gap-2 rounded-lg border border-[#E2E4ED] bg-white px-2 py-1.5 text-sm text-[#1B2559] transition-colors hover:border-[#6366F1] hover:bg-[#6366F1]/[0.04] active:cursor-grabbing"
                    draggable
                    unselectable="on"
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', type);
                      e.dataTransfer.effectAllowed = 'move';
                      onDragStartWidget(type);
                    }}
                    onDragEnd={() => onDragEndWidget?.()}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#F4F5FA] text-[#8B8FA7] transition-colors group-hover:bg-white group-hover:text-[#6366F1]">
                      <Icon size={14} />
                    </span>
                    <span className="flex-1">{def.label}</span>
                    <span className="text-[10px] tabular-nums text-[#B0B4C5]">
                      {def.w}×{def.h}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
